import {
  IUserReportAddResult,
  TStiReportData,
} from '@infotech-soe/merps/types/Reports';
import {
  ActionArgumentsP,
  ActionName,
  CategoryActionP,
  DbId,
  IContextMenuItemRefreshRules,
  IDatasetParsed,
  IDisplayModeParsed,
  PrintActionP,
  ReturnItemsP,
  ShowCategoryActionP,
  TDatabaseId,
  tg,
} from '@infotech-soe/metaschema';

import { AppDispatch } from '../..';
import IApiProvider, {
  TActionArgsValues,
  TActionResult,
} from '../../../infrastructure/providers/abstract/IApiProvider';
import MerpsApiProvider from '../../../infrastructure/providers/pb-adapter/MerpsApiProvider';
import CachedSchemaRepository from '../../../infrastructure/schemas/storage/CachedSchemaRepository';
import ISchemaRepository from '../../../infrastructure/schemas/storage/ISchemaRepository';
import { CaptionType } from '../../../models/caption/Caption';
import {
  CategoryOpenMode,
  CategoryUI,
} from '../../../models/category/category';
import { MultiStepFormSnapshot } from '../../../models/form/form';
import CategoryInParamsExtractorService from '../../../services/category/CategoryInParamsExtractorService';
import ICategoryInParamsExtractorService from '../../../services/category/ICategoryInParamsExtractorService';
import { processShowCategoryInParams } from '../../../services/commands/impl/processShowCategoryInParams';
import { REPORTS_SELECT_ACTION } from '../../../services/commands/impl/ReportsCommandImpl';
import { rememberLastReport } from '../../../services/commands/reportSelectionCache';
import { RefreshStrategy } from '../../../services/commands/wrap/CategoryCommand';
import { executePrintAction } from '../../../services/common/executePrintAction';
import { downloadFileFromBlob } from '../../../services/common/files';
import ContextService from '../../../services/context/ContextService';
import IContextService, {
  ClientContext,
  ClientContextKeys,
} from '../../../services/context/IContextService';
import { getAppDisplayModeName } from '../../../services/form/builders/controls/helpers/getAppDisplayModeName';
import { FormSourceType } from '../../../services/form/view-model/FormSource';
import IIntlService from '../../../services/intl/IIntlService';
import IntlService from '../../../services/intl/IntlService';
import UniqueId from '../../../services/uid/UniqueId';
import { AppState } from '../../reducers/root';
import { getProcessStateForCategory } from '../../selectors/actions';
import { getBalanceContentTab } from '../../selectors/balance';
import {
  getCategoryOpenMode,
  getCategoryState,
} from '../../selectors/category';
import { getDatasetCategoryId, getDatasetState } from '../../selectors/dataset';
import {
  getIsOpen as reviewIsOpen,
  getNextAction as reviewNextAction,
} from '../../selectors/review';
import { getSessionApplication } from '../../selectors/session';
import actionProgress from '../../slices/actionProgress';
import actions from '../../slices/actions';
import {
  BalanceAction,
  BalanceCategory,
  BalanceCheckReportsCategory,
  BalanceCheckReportsSpecsCategory,
  BalanceIndexSubValuesCategory,
  BalanceIndexValuesCategory,
} from '../../slices/balance';
import category from '../../slices/category';
import content from '../../slices/content';
import dataset from '../../slices/dataset';
import dictionary, { DictionaryEffectType } from '../../slices/dictionary';
import form from '../../slices/form';
import pivot from '../../slices/pivot';
import review from '../../slices/review';
import { dispatchBalanceEditValues } from '../../utils/dispatchBalanceEditValues';
import {
  apiCall,
  createAppListener,
  onceInit,
  postDispatch,
} from '../listenerHelpers';

const actionsListener = createAppListener();

const apiProvider: IApiProvider = new MerpsApiProvider();
const schemaRepository: ISchemaRepository = new CachedSchemaRepository();
const intlService: IIntlService = new IntlService();
const contextService: IContextService = new ContextService();
const inParamsExtractor: ICategoryInParamsExtractorService =
  new CategoryInParamsExtractorService();

actionsListener.startListening(
  onceInit((_state, dispatch) => {
    apiProvider.actions.addListenerOnActionProgress(payload => {
      dispatch(actionProgress.update(payload));
    });
  })
);

const hasNextStep = (result: TActionResult, nextActionProperty?: string) =>
  result &&
  (Object.values(result)
    .filter(val => val instanceof Object)
    .find((obj: any) => obj.action) ||
    (nextActionProperty && result[nextActionProperty]));

const getNextActionAndStateNames = (
  returns: ReturnItemsP
): {
  nextActionProperty: string | undefined;
  stateNames: Record<string, string>;
} => {
  let nextActionProperty;
  const stateNames: Record<string, string> = {};

  for (const [objName, obj] of Object.entries(returns)) {
    if ('action' in obj && obj.action) {
      nextActionProperty = objName;
    } else if ('state' in obj && obj.state) {
      stateNames[objName] = obj.state;
    }
  }

  return { nextActionProperty, stateNames };
};

const refreshDataset = (
  datasetId: UniqueId,
  categoryId: UniqueId,
  hook: boolean | undefined,
  refreshRules: IContextMenuItemRefreshRules | undefined,
  refreshStrategy: RefreshStrategy | undefined,
  ids: DbId[] | undefined,
  dispatch: AppDispatch
): void => {
  if (!hook) {
    if (refreshRules) {
      dispatch(
        category.refreshWithRules({
          datasetId,
          refreshRules,
          rowId: refreshStrategy === RefreshStrategy.NEW ? ids?.[0] : undefined,
        })
      );
    } else if (refreshStrategy) {
      dispatch(
        dataset.refreshWithStrategy({
          datasetId,
          strategy: refreshStrategy,
          ids: ids || [],
        })
      );
    } else {
      dispatch(dataset.update({ datasetId }));
    }
  } else {
    dispatch(category.load({ categoryId }));
  }
};

const showCategory = async (
  datasetId: UniqueId,
  action: ShowCategoryActionP,
  actionName: ActionName,
  context: ClientContext,
  hook: boolean,
  refreshSnapshot: MultiStepFormSnapshot | undefined,
  state: AppState,
  dispatch: AppDispatch
): Promise<void> => {
  const categoryId = getDatasetCategoryId(state, datasetId);
  const categoryState = getCategoryState(state, categoryId);

  const datasetName = categoryState.datasets[datasetId].name;

  let displayModeSchema: IDisplayModeParsed | undefined;

  if (context.has('DisplayMode')) {
    displayModeSchema = await schemaRepository.getDisplayMode(
      context.get('MasterCategory') || context.get('Category'),
      context.get('DisplayMode')
    );
  }

  let datasetSchema: IDatasetParsed | undefined;

  if (displayModeSchema) {
    datasetSchema = displayModeSchema.datasets[datasetName];
  }

  const datasetCurrentState = getDatasetState(state, datasetId);

  const processState = getProcessStateForCategory(state, categoryId);
  const inParams = action.InParams
    ? await processShowCategoryInParams(
        context.toServerContext(),
        action.InParams,
        datasetSchema ? datasetSchema.source : undefined,
        datasetCurrentState,
        processState
      )
    : {};
  const displayModeName = getAppDisplayModeName(
    getSessionApplication(state),
    action.DisplayMode
  );

  if (action.Category === BalanceCheckReportsSpecsCategory) {
    const existingTabId = getBalanceContentTab(
      state,
      action.Category,
      inParams
    );
    if (existingTabId) {
      dispatch(content.activate({ id: existingTabId }));
      return;
    }

    dispatch(
      category.openDisplayMode({
        category: action.Category,
        displayMode: displayModeName,
        ui: { mode: CategoryOpenMode.CONTENT },
        inParams,
      })
    );
    return;
  }

  const openDisplayModeUIType: CategoryUI = {
    mode: CategoryOpenMode.DICTIONARY,
    multiSelect: action.MultiSelect || false,
    hideTree: action.HideTree || false,
    fullHierarchy: action.FullHierarchy || false,
    hideCheckbox: action.HideCheckbox || false,
    effect: action.NextAction
      ? {
          type: DictionaryEffectType.PROCESS_ACTION,
          action: actionName,
          category: action.Category,
          dataset: datasetId,
          hook,
          refresh: refreshSnapshot,
        }
      : refreshSnapshot?.refreshRules &&
          'refreshOnClose' in refreshSnapshot.refreshRules &&
          refreshSnapshot.refreshRules.refreshOnClose
        ? {
            type: DictionaryEffectType.REFRESH_ON_CLOSE,
            dataset: datasetId,
            refresh: refreshSnapshot,
          }
        : {
            type: DictionaryEffectType.DO_NOTHING,
          },
  };

  dispatch(
    category.openDisplayMode({
      category: action.Category,
      displayMode: displayModeName,
      ui: openDisplayModeUIType,
      inParams,
      nextAction: action.NextAction,
    })
  );
};

const download = async (
  action: ActionName,
  args: TActionArgsValues,
  context: ClientContext
): Promise<any> => {
  const result = await apiProvider.actions.executeAction(
    action,
    context.toServerContext(),
    args
  );

  if (result) {
    const buffer = await apiProvider.streams.read(result.data.id);

    downloadFileFromBlob(result.fileName, buffer);
  }

  return result;
};

const print = async (
  action: ActionName,
  args: TActionArgsValues,
  context: ClientContext,
  actionSchema: PrintActionP,
  datasetId: UniqueId,
  dispatch: AppDispatch
): Promise<
  | IUserReportAddResult
  | {
      data: TStiReportData['data'];
      template: string;
      variables?: Record<string, any>;
    }
> => {
  const reportResult = await executePrintAction(
    action,
    context,
    args,
    actionSchema
  );

  if (
    'data' in reportResult &&
    reportResult.data &&
    'template' in reportResult &&
    reportResult.template
  ) {
    const { data, template, vars, events, parseDates } = reportResult;
    dispatch(
      review.open({
        data,
        template,
        variables: vars,
        events,
        parseDates,
        nextAction: actionSchema.NextAction,
        datasetId,
      })
    );
  }
  return reportResult;
};

const execute = async (
  action: ActionName,
  args: TActionArgsValues,
  context: ClientContext,
  datasetId: UniqueId,
  state: AppState,
  dispatch: AppDispatch
): Promise<any> => {
  const categoryName = context.get('Category');
  const actionSchema = await schemaRepository.getAction(categoryName, action);
  const actionName = await intlService.getActionName(
    categoryName,
    actionSchema.resId ?? action
  );
  dispatch(actionProgress.start({ actionName }));

  let result;
  try {
    result = await apiProvider.actions.executeAction(
      action,
      context.toServerContext(),
      args
    );
  } finally {
    dispatch(actionProgress.finish());
  }

  const inParams = await inParamsExtractor.fromCommand(
    context,
    action,
    result ?? {}
  );

  if (inParams) {
    const filteredParams = await inParamsExtractor.filterByDisplay(
      context.get('MasterCategory'),
      context.get('DisplayMode'),
      inParams
    );
    dispatch(
      category.updateInParams({
        categoryId: getDatasetCategoryId(state, datasetId),
        inParams: filteredParams,
      })
    );
  }

  return result;
};

const getProcessState = (
  prevActionResult: Record<string, any>,
  stateNames: Record<string, string>,
  categoryId: UniqueId,
  state: AppState
): Record<string, any> => {
  const resultingState = { ...getProcessStateForCategory(state, categoryId) };

  for (const [propName, prop] of Object.entries(prevActionResult)) {
    if (Object.keys(stateNames).includes(propName)) {
      resultingState[stateNames[propName]] = prop;
    }
  }

  return resultingState;
};

const getNextActionArgs = (
  args: ActionArgumentsP,
  processState: Record<string, any>
): Record<string, any> => {
  const nextActionArgs: Record<string, any> = {};

  for (const [argName, arg] of Object.entries(args)) {
    if ('fromState' in arg && arg.fromState) {
      const path = arg.fromState;
      const pathParts = path.split('.');
      const stateModule = String(pathParts.shift());
      const newPath = pathParts.join('.');

      nextActionArgs[argName] =
        pathParts.length > 1
          ? processState[stateModule][newPath]
          : processState[path];
    } else if ('literal' in arg) {
      nextActionArgs[argName] = arg.literal;
    }
  }

  return nextActionArgs;
};

const executeAction = async (
  action: ActionName,
  context: ClientContext,
  datasetId: UniqueId,
  prevActionResult: Record<string, any> | undefined,
  hook: boolean | undefined,
  refreshRules: IContextMenuItemRefreshRules | undefined,
  refreshStrategy: RefreshStrategy | undefined,
  getState: () => AppState,
  dispatch: AppDispatch
): Promise<void> => {
  const state = getState();
  const categoryName = context.get('Category');
  const actionSchema = await schemaRepository.getAction(categoryName, action);

  const { nextActionProperty, stateNames } =
    prevActionResult && 'Returns' in actionSchema && actionSchema.Returns
      ? getNextActionAndStateNames(actionSchema.Returns)
      : ({} as any);

  const afterReviewAction = reviewIsOpen(state)
    ? undefined
    : reviewNextAction(state);

  if (
    prevActionResult &&
    !prevActionResult[nextActionProperty] &&
    !afterReviewAction
  ) {
    if (!(
      tg.isPrintAction(actionSchema) &&
      tg.hasProperty(actionSchema, 'NextAction')
    )) {
      const currentCategoryId = getDatasetCategoryId(state, datasetId);
      const openMode = getCategoryOpenMode(state, currentCategoryId);
      if (openMode === CategoryOpenMode.HIDDEN) {
        dispatch(category.close({ categoryId: currentCategoryId }));
      } else {
        refreshDataset(
          datasetId,
          currentCategoryId,
          !!hook,
          refreshRules,
          refreshStrategy,
          Object.values(prevActionResult),
          dispatch
        );
        dispatch(actions.clearProcessState({ categoryId: currentCategoryId }));
      }
    }
    return;
  }

  const nextActionSchema =
    nextActionProperty && prevActionResult?.[nextActionProperty]
      ? await schemaRepository.getAction(
          categoryName,
          prevActionResult[nextActionProperty]
        )
      : afterReviewAction
        ? await schemaRepository.getAction(categoryName, afterReviewAction)
        : actionSchema;

  const nextAction =
    afterReviewAction ||
    (nextActionProperty && prevActionResult
      ? prevActionResult[nextActionProperty]
      : action);

  const categoryId = getDatasetCategoryId(state, datasetId);

  const processState =
    prevActionResult && stateNames
      ? getProcessState(prevActionResult, stateNames, categoryId, state)
      : getProcessStateForCategory(state, categoryId);

  dispatch(actions.updateProcessState({ categoryId, processState }));

  if (nextActionSchema.Type === 'ShowCategory') {
    await showCategory(
      datasetId,
      nextActionSchema,
      nextAction,
      context,
      !!hook,
      {
        refresh: refreshStrategy,
        refreshRules,
        prevState: processState,
      },
      getState(),
      dispatch
    );
    if (afterReviewAction) {
      dispatch(review.finishReview());
    }

    return;
  }

  const actionArgs: Record<string, any> = getNextActionArgs(
    nextActionSchema.Args,
    processState
  );

  if (tg.isActionWithForm(nextActionSchema)) {
    dispatch(
      form.create({
        type: tg.isPrintAction(nextActionSchema)
          ? FormSourceType.PRINT
          : FormSourceType.FORM,
        multiStep: {
          prevState: processState,
          refresh: refreshStrategy,
          refreshRules,
        },
        args: actionArgs,
        category: categoryName,
        action: nextAction,
        dataset: datasetId,
        command: '',
        title: {
          type: CaptionType.EXTERNAL,
          caption: await intlService.getCategoryFieldTitle(
            categoryName,
            nextActionSchema.resId || nextActionSchema.Form
          ),
        },
        context,
        hook: !!hook,
      })
    );
    if (afterReviewAction) {
      dispatch(review.finishReview());
    }
    return;
  }

  const result = await (tg.isPrintAction(nextActionSchema)
    ? print(
        nextAction,
        actionArgs,
        context,
        nextActionSchema,
        datasetId,
        dispatch
      )
    : nextActionSchema.Type === 'Download'
      ? download(nextAction, actionArgs, context)
      : execute(
          nextAction,
          actionArgs,
          context,
          datasetId,
          getState(),
          dispatch
        ));

  if (afterReviewAction) {
    dispatch(review.finishReview());
  }

  if (nextActionSchema.Type === 'Pivot') {
    dispatch(
      pivot.open({ dataset: datasetId, command: nextAction, args: actionArgs })
    );
  }

  if (
    [
      BalanceCategory,
      BalanceIndexValuesCategory,
      BalanceIndexSubValuesCategory,
      BalanceCheckReportsCategory,
      BalanceCheckReportsSpecsCategory,
    ].includes(context.get('MasterCategory')) &&
    nextAction === BalanceAction.EDIT_VALUES.name &&
    result?.data
  ) {
    dispatchBalanceEditValues(result, datasetId, getState(), dispatch);
  }

  const { nextActionProperty: newNextAction } =
    result && 'Returns' in nextActionSchema
      ? getNextActionAndStateNames(nextActionSchema.Returns)
      : {};

  if (hasNextStep(result, newNextAction)) {
    await executeAction(
      nextAction,
      context,
      datasetId,
      result,
      hook,
      refreshRules,
      refreshStrategy,
      getState,
      dispatch
    );
  }

  if (prevActionResult || afterReviewAction) {
    refreshDataset(
      datasetId,
      categoryId,
      !!hook,
      refreshRules,
      refreshStrategy,
      undefined,
      dispatch
    );
  }
};

actionsListener.startListening(
  postDispatch(
    actions.processShowCategory,
    (
      {
        outParams,
        category: categoryName,
        dataset: datasetId,
        action,
        refreshRules,
        refresh,
        hook,
        dictionaryId,
      },
      state,
      dispatch
    ) => {
      if (!categoryName) {
        const categoryId = getDatasetCategoryId(state, datasetId);

        dispatch(actions.clearProcessState({ categoryId }));
        return;
      }

      dispatch(
        actions.submitShowCategory({
          outParams,
          action,
          dataset: datasetId,
          dictionaryId,
          hook,
          refreshRules,
          refresh,
        })
      );
    }
  )
);

actionsListener.startListening(
  apiCall(
    {
      request: actions.processMultistep,
      success: actions.processMultistepSuccess,
      failure: actions.processMultistepFailure,
    },
    async (
      {
        datasetId,
        prevActionResult,
        category: categoryName,
        action,
        datasets,
        refreshRules,
        refresh,
        hook,
      },
      _state,
      dispatch,
      getState
    ) => {
      const context = await contextService.getContext(
        categoryName,
        datasets,
        datasetId
      );
      await executeAction(
        action,
        context,
        datasetId,
        prevActionResult,
        hook,
        refreshRules,
        refresh,
        getState,
        dispatch
      );
    }
  )
);

actionsListener.startListening(
  apiCall(
    {
      request: actions.continueMultistep,
      success: actions.continueMultistepSuccess,
      failure: actions.continueMultistepFailure,
    },
    async (
      { action, args, context, source, hook, formId },
      state,
      dispatch
    ) => {
      const actionSchema = await schemaRepository.getAction(
        context.get('Category'),
        action
      );

      if (actionSchema.Type === 'ShowCategory') {
        if (source.type !== FormSourceType.FORM) {
          return;
        }

        await showCategory(
          source.dataset,
          actionSchema,
          action,
          context,
          !!hook,
          source.multiStep,
          state,
          dispatch
        );
      }

      const result = await (tg.isPrintAction(actionSchema)
        ? print(action, args, context, actionSchema, source.dataset, dispatch)
        : actionSchema.Type === 'Download'
          ? download(action, args, context)
          : execute(action, args, context, source.dataset, state, dispatch));

      if (formId) {
        dispatch(form.close({ formId }));
      }

      if (actionSchema.Type === 'Pivot') {
        dispatch(
          pivot.open({ dataset: source.dataset, command: action, args })
        );
      }

      const categoryName = getCategoryState(state, source.categoryId);

      if (!result) {
        const openMode = getCategoryOpenMode(state, source.categoryId);
        if (openMode === CategoryOpenMode.HIDDEN) {
          dispatch(category.close({ categoryId: source.categoryId }));
        } else {
          refreshDataset(
            source.dataset,
            source.categoryId,
            false,
            source.multiStep?.refreshRules,
            source.multiStep?.refresh,
            [context.get('Id')],
            dispatch
          );
          dispatch(
            actions.clearProcessState({ categoryId: source.categoryId })
          );
        }
        return;
      }

      const datasets = state.dataset;

      dispatch(
        actions.processMultistep({
          prevActionResult: result,
          action,
          category: categoryName,
          datasets,
          datasetId: source.dataset,
          hook: !!hook,
          refresh: source.multiStep?.refresh,
          refreshRules: source.multiStep?.refreshRules,
        })
      );
    }
  )
);

actionsListener.startListening(
  apiCall(
    {
      request: actions.execute,
      success: actions.executeSuccess,
      failure: actions.executeFailure,
    },
    async (
      { datasetId, action, editedRowContext, newValue },
      state,
      dispatch
    ) => {
      const categoryId = getDatasetCategoryId(state, datasetId);
      const categoryState = getCategoryState(state, categoryId);
      const datasetState = state.dataset;
      const context = await contextService.getContext(
        categoryState,
        datasetState,
        datasetId
      );
      const mergedContext = editedRowContext
        ? new ClientContext([
            ...context,
            ...(Object.entries(editedRowContext) as Array<
              [ClientContextKeys, unknown]
            >),
          ])
        : context;
      const actionSchema = await schemaRepository.getAction(
        context.get('Category'),
        action
      );
      const getRefreshStrategy = (
        checkedActionSchema: CategoryActionP
      ): RefreshStrategy | undefined => {
        if (!('RefreshMode' in checkedActionSchema)) {
          return;
        }
        switch (checkedActionSchema.RefreshMode) {
          case 'All':
            return RefreshStrategy.ALL;
          case 'Current':
            return RefreshStrategy.CURRENT;
          case 'None':
            return RefreshStrategy.NONE;
        }
      };
      await execute(
        action,
        { newValue },
        mergedContext,
        datasetId,
        state,
        dispatch
      );
      refreshDataset(
        datasetId,
        categoryId,
        false,
        undefined,
        getRefreshStrategy(actionSchema),
        [editedRowContext.Id as TDatabaseId],
        dispatch
      );
    }
  )
);

actionsListener.startListening(
  apiCall(
    {
      request: actions.submitShowCategory,
      success: actions.submitShowCategorySuccess,
      failure: actions.submitShowCategoryFailure,
    },
    async (
      {
        outParams,
        action,
        dataset: datasetId,
        dictionaryId,
        hook,
        refreshRules,
        refresh: refreshStrategy,
      },
      state,
      dispatch,
      getState
    ) => {
      const categoryId = getDatasetCategoryId(state, datasetId);
      const categoryState = getCategoryState(state, categoryId);
      const datasetState = state.dataset;
      const context = await contextService.getContext(
        categoryState,
        datasetState,
        datasetId
      );

      const currentDataset = datasetState[datasetId];

      if (!currentDataset.source) {
        refreshDataset(
          datasetId,
          categoryId,
          !!hook,
          refreshRules,
          refreshStrategy,
          undefined,
          dispatch
        );
        dispatch(actions.clearProcessState({ categoryId }));
        return;
      }

      const actionSchema =
        action && action !== REPORTS_SELECT_ACTION
          ? ((await schemaRepository.getAction(
              currentDataset.source.category,
              action
            )) as ShowCategoryActionP)
          : null;

      const processState = { ...getProcessStateForCategory(state, categoryId) };

      if (actionSchema?.OutParams) {
        for (const [outParamName, outParam] of Object.entries(
          actionSchema.OutParams
        )) {
          if (outParam.state && outParams[outParamName] !== undefined) {
            processState[outParam.state] = outParams[outParamName];
          }
        }
      }

      dispatch(actions.updateProcessState({ categoryId, processState }));

      if (action === REPORTS_SELECT_ACTION && outParams.code) {
        rememberLastReport(
          context.get('Category'),
          context.get('DisplayMode'),
          outParams.code
        );
      }

      const nextAction =
        action !== REPORTS_SELECT_ACTION
          ? actionSchema?.NextAction
          : outParams.code;

      if (nextAction) {
        const nextActionSchema = await schemaRepository.getAction(
          currentDataset.source.category,
          nextAction
        );

        if (nextActionSchema.Type === 'ShowCategory') {
          dispatch(dictionary.close({ id: dictionaryId }));
          await showCategory(
            datasetId,
            nextActionSchema,
            nextAction,
            context,
            !!hook,
            {
              prevState: processState,
              refreshRules,
              refresh: refreshStrategy,
            },
            getState(),
            dispatch
          );
          return;
        }

        const nextActionArgs = getNextActionArgs(
          nextActionSchema.Args,
          processState
        );

        if (tg.isActionWithForm(nextActionSchema)) {
          dispatch(dictionary.close({ id: dictionaryId }));
          dispatch(
            form.create({
              type: tg.isPrintAction(nextActionSchema)
                ? FormSourceType.PRINT
                : FormSourceType.FORM,
              multiStep: {
                prevState: processState,
                refresh: refreshStrategy,
                refreshRules,
              },
              args: nextActionArgs,
              category: currentDataset.source.category,
              action: nextAction,
              dataset: datasetId,
              command: '',
              title: {
                type: CaptionType.EXTERNAL,
                caption: await intlService.getCategoryFieldTitle(
                  context.get('Category'),
                  nextActionSchema.resId || nextActionSchema.Form
                ),
              },
              context,
            })
          );

          return;
        }

        const result = await (tg.isPrintAction(nextActionSchema)
          ? print(
              nextAction,
              nextActionArgs,
              context,
              nextActionSchema,
              datasetId,
              dispatch
            )
          : nextActionSchema.Type === 'Download'
            ? download(nextAction, nextActionArgs, context)
            : execute(
                nextAction,
                nextActionArgs,
                context,
                datasetId,
                getState(),
                dispatch
              ));
        dispatch(dictionary.close({ id: dictionaryId }));

        if (nextActionSchema.Type === 'Pivot') {
          dispatch(
            pivot.open({
              dataset: datasetId,
              command: nextAction,
              args: nextActionArgs,
            })
          );
        }

        const { nextActionProperty } = getNextActionAndStateNames(
          nextActionSchema.Returns
        );

        if (!nextActionProperty) {
          refreshDataset(
            datasetId,
            categoryId,
            hook,
            refreshRules,
            refreshStrategy,
            result ? Object.values(result) : undefined,
            dispatch
          );
          dispatch(actions.clearProcessState({ categoryId }));
          return;
        }

        await executeAction(
          nextAction,
          context,
          datasetId,
          result,
          hook,
          refreshRules,
          refreshStrategy,
          getState,
          dispatch
        );
      }
    }
  )
);

actionsListener.startListening(
  postDispatch(actionProgress.cancel, () => {
    apiProvider.actions.cancelAction();
  })
);

export default actionsListener.middleware;
